import { pipe, dual } from '../../Function/dist/effect-Function.esm.js';
import { map, withSTMRuntime, retry, succeed, effect } from './core.esm.js';
import { make as make$1, get as get$1, update, unsafeGet, modify, unsafeSet } from './tRef.esm.js';
import { match, map as map$1, none, some } from '../../Option/dist/effect-Option.esm.js';
import { of, prepend, filter, splitAt } from '../../ReadonlyArray/dist/effect-ReadonlyArray.esm.js';
import { empty as empty$1, set, get, isEmpty as isEmpty$1, isNonEmpty as isNonEmpty$1, headOption, reduce, getOrder, remove } from '../../SortedMap/dist/effect-SortedMap.esm.js';

/** @internal */
const TPriorityQueueSymbolKey = "effect/TPriorityQueue";

/** @internal */
const TPriorityQueueTypeId = /*#__PURE__*/Symbol.for(TPriorityQueueSymbolKey);

/** @internal */
const tPriorityQueueVariance = {
  _A: _ => _
};

/** @internal */
class TPriorityQueueImpl {
  [TPriorityQueueTypeId] = tPriorityQueueVariance;
  constructor(ref) {
    this.ref = ref;
  }
}

/** @internal */
const empty = order => pipe(make$1(empty$1(order)), map(ref => new TPriorityQueueImpl(ref)));

/** @internal */
const fromIterable = order => iterable => pipe(make$1(Array.from(iterable).reduce((map, value) => pipe(map, set(value, pipe(map, get(value), match({
  onNone: () => of(value),
  onSome: prepend(value)
})))), empty$1(order))), map(ref => new TPriorityQueueImpl(ref)));

/** @internal */
const isEmpty = self => map(get$1(self.ref), isEmpty$1);

/** @internal */
const isNonEmpty = self => map(get$1(self.ref), isNonEmpty$1);

/** @internal */
const make = order => (...elements) => fromIterable(order)(elements);

/** @internal */
const offer = /*#__PURE__*/dual(2, (self, value) => update(self.ref, map => set(map, value, match(get(map, value), {
  onNone: () => of(value),
  onSome: prepend(value)
}))));

/** @internal */
const offerAll = /*#__PURE__*/dual(2, (self, values) => update(self.ref, map => Array.from(values).reduce((map, value) => set(map, value, match(get(map, value), {
  onNone: () => of(value),
  onSome: prepend(value)
})), map)));

/** @internal */
const peek = self => withSTMRuntime(runtime => {
  const map = unsafeGet(self.ref, runtime.journal);
  return match(headOption(map), {
    onNone: () => retry,
    onSome: elements => succeed(elements[0])
  });
});

/** @internal */
const peekOption = self => modify(self.ref, map => [map$1(headOption(map), elements => elements[0]), map]);

/** @internal */
const removeIf = /*#__PURE__*/dual(2, (self, predicate) => retainIf(self, a => !predicate(a)));

/** @internal */
const retainIf = /*#__PURE__*/dual(2, (self, predicate) => update(self.ref, map => reduce(map, empty$1(getOrder(map)), (map, value, key) => {
  const filtered = filter(value, predicate);
  return filtered.length > 0 ? set(map, key, filtered) : remove(map, key);
})));

/** @internal */
const size = self => modify(self.ref, map => [reduce(map, 0, (n, as) => n + as.length), map]);

/** @internal */
const take = self => withSTMRuntime(runtime => {
  const map = unsafeGet(self.ref, runtime.journal);
  return match(headOption(map), {
    onNone: () => retry,
    onSome: values => {
      const head = values[1][0];
      const tail = values[1].slice(1);
      unsafeSet(self.ref, tail.length > 0 ? set(map, head, tail) : remove(map, head), runtime.journal);
      return succeed(head);
    }
  });
});

/** @internal */
const takeAll = self => modify(self.ref, map => {
  const builder = [];
  for (const entry of map) {
    builder.push(...entry[1]);
  }
  return [builder, empty$1(getOrder(map))];
});

/** @internal */
const takeOption = self => effect(journal => {
  const map = pipe(self.ref, unsafeGet(journal));
  return match(headOption(map), {
    onNone: () => none(),
    onSome: ([key, value]) => {
      const tail = value.slice(1);
      unsafeSet(self.ref, tail.length > 0 ? set(map, key, tail) : remove(map, key), journal);
      return some(value[0]);
    }
  });
});

/** @internal */
const takeUpTo = /*#__PURE__*/dual(2, (self, n) => modify(self.ref, map => {
  const builder = [];
  const iterator = map[Symbol.iterator]();
  let updated = map;
  let index = 0;
  let next;
  while ((next = iterator.next()) && !next.done && index < n) {
    const [key, value] = next.value;
    const [left, right] = pipe(value, splitAt(n - index));
    builder.push(...left);
    if (right.length > 0) {
      updated = set(updated, key, right);
    } else {
      updated = remove(updated, key);
    }
    index = index + left.length;
  }
  return [builder, updated];
}));

/** @internal */
const toChunk = self => modify(self.ref, map => {
  const builder = [];
  for (const entry of map) {
    builder.push(...entry[1]);
  }
  return [builder, map];
});

/** @internal */
const toReadonlyArray = self => modify(self.ref, map => {
  const builder = [];
  for (const entry of map) {
    builder.push(...entry[1]);
  }
  return [builder, map];
});

export { TPriorityQueueImpl, TPriorityQueueTypeId, empty, fromIterable, isEmpty, isNonEmpty, make, offer, offerAll, peek, peekOption, removeIf, retainIf, size, take, takeAll, takeOption, takeUpTo, toChunk, toReadonlyArray };
